import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Edit,
  Trash,
  Scale,
  PlayCircle,
  StickyNote,
  Receipt,
  AlertCircle,
  Eye,
  Power,
  ArrowUp,
  ArrowDown,
  Copy,
  Send,
  CheckCircle2,
  XCircle,
  MoreVertical,
  PlusCircle,
  Save,
  Upload,
  Trash2,
  Archive,
  RotateCcw,
  Mail,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Calendar,
  Inbox,
  FileText,
  Package,
  User,
  Building2,
  ShieldAlert,
  ShieldCheck,
  ClipboardList,
  Truck,
  AlertTriangle,
  Ban,
  UserX,
  CheckCheck,
  FolderArchive,
  X,
  Search,
  Loader2,
  Filter,
  Download,
  Hash,
} from 'lucide-react';
import { Button } from './ui/button';
import Pagination from './Pagination';
import { cn } from '../lib/utils';

const ICON_MAP = {
  Edit,
  Trash,
  Eye,
  Power,
  Copy,
  Send,
  CheckCircle2,
  XCircle,
  MoreVertical,
  PlusCircle,
  Save,
  Upload,
  Trash2,
  Archive,
  RotateCcw,
  Mail,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
  Calendar,
  Inbox,
  FileText,
  Package,
  User,
  Building2,
  ShieldAlert,
  ShieldCheck,
  ClipboardList,
  Truck,
  AlertTriangle,
  Ban,
  UserX,
  CheckCheck,
  FolderArchive,
  X,
  Search,
  Loader2,
  Filter,
  Download,
  Hash,
  Scale,
  PlayCircle,
  StickyNote,
  Receipt,
};

const DataTable = ({
  data,
  columns,
  isLoading,
  onAction,
  page,
  limit,
  totalRecords,
  onPageChange,
  onLimitChange,
  sort,
  onSort,
  emptyMessage = 'No hay datos disponibles',
  emptyHint = 'Intenta ajustar tus filtros o verifica si hay datos para mostrar.',
}) => {
  const tableRowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.04, type: 'spring', stiffness: 300, damping: 20 },
    }),
    exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
  };

  if (isLoading) {
    return (
      <div className="p-4 bg-card border border-border rounded-lg">
        <div className="space-y-3">
          <div className="h-8 bg-muted rounded w-full animate-pulse" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-muted/60 rounded w-full animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center p-6 bg-card border border-border rounded-lg shadow-sm text-center min-h-[200px]"
      >
        <div className="w-14 h-14 bg-primary/10 text-primary rounded-full flex items-center justify-center mb-4">
          <AlertCircle className="h-7 w-7" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-1">{emptyMessage}</h3>
        <p className="text-muted-foreground text-sm max-w-xs">{emptyHint}</p>
      </motion.div>
    );
  }

  const actionsColumn = columns.find(
    (col) => col.key === 'actions' || col.id === 'actions' || col.key === 'opciones' || col.id === 'opciones',
  );
  const dataColumns = columns.filter(
    (col) =>
      col.key !== 'actions' &&
      col.id !== 'actions' &&
      col.key !== 'opciones' &&
      col.id !== 'opciones',
  );

  return (
    <div className="overflow-x-auto w-full rounded-lg shadow-sm border border-border custom-scrollbar">
      <table className="w-full table-auto divide-y divide-border bg-background text-sm">
        <thead className="bg-muted/40 sticky top-0 z-10 backdrop-blur-sm">
          <tr>
            {actionsColumn && (
              <th className="px-3 py-3 text-center font-semibold text-foreground w-[120px]">
                {actionsColumn.label || 'Acciones'}
              </th>
            )}
            {!actionsColumn && (
              <th className="px-3 py-3 text-center font-semibold text-foreground w-[120px]">Acciones</th>
            )}
            {dataColumns.map((column) => {
              const colId = column.id || column.key;
              const sortKey = sort ? Object.keys(sort)[0] : null;
              const sortDir = sortKey ? sort[sortKey] : null;
              const isSorted = colId === sortKey;
              const isAsc = isSorted && (sortDir === 1 || sortDir === 'asc');
              const isDesc = isSorted && (sortDir === -1 || sortDir === 'desc');
              const sortable = column.sortable && onSort;
              return (
                <th
                  key={colId}
                  className={cn(
                    'px-4 py-3 text-left font-semibold text-foreground whitespace-nowrap select-none',
                    sortable && 'cursor-pointer hover:bg-muted/60',
                  )}
                  style={{ width: column.width || 'auto' }}
                  onClick={sortable ? () => onSort(colId) : undefined}
                >
                  <span className="inline-flex items-center gap-1">
                    {column.label}
                    {isAsc && <ArrowUp className="h-3.5 w-3.5 text-primary" />}
                    {isDesc && <ArrowDown className="h-3.5 w-3.5 text-primary" />}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <AnimatePresence mode="popLayout">
            {data.map((row, rowIndex) => (
              <motion.tr
                key={row._id || row.id || `row-${rowIndex}`}
                custom={rowIndex}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={tableRowVariants}
                layoutId={`row-${row._id || row.id || rowIndex}`}
                className="transition-colors duration-200 hover:bg-muted/30 border-b border-border last:border-b-0"
              >
                {actionsColumn ? (
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center items-center gap-1">
                      {actionsColumn.actions?.map((action) => {
                        if (action.showWhen && !action.showWhen(row)) return null;
                        const Icon = ICON_MAP[action.icon] || Edit;
                        const actionKey = action.key || action.id;
                        const isDisabled =
                          typeof action.disabledWhen === 'function' ? !!action.disabledWhen(row) : false;
                        return (
                          <Button
                            key={actionKey}
                            onClick={() => !isDisabled && onAction?.(actionKey, row)}
                            variant="ghost"
                            size="icon"
                            disabled={isDisabled}
                            title={isDisabled ? action.disabledTooltip || action.tooltip : action.tooltip || action.label}
                            className={cn(
                              'h-8 w-8',
                              action.className || 'hover:bg-foreground hover:text-background',
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </Button>
                        );
                      })}
                    </div>
                  </td>
                ) : (
                  <td className="py-2 px-2 text-center">
                    <div className="flex justify-center items-center gap-1">
                      <Button
                        onClick={() => onAction?.('edit', row)}
                        variant="ghost"
                        size="icon"
                        title="Editar"
                        className="h-8 w-8 hover:bg-foreground hover:text-background"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        onClick={() => onAction?.('delete', row)}
                        variant="ghost"
                        size="icon"
                        title="Eliminar"
                        className="h-8 w-8 hover:bg-destructive hover:text-destructive-foreground"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                )}
                {dataColumns.map((column) => (
                  <td
                    key={`${row._id || row.id || rowIndex}-${column.id || column.key}`}
                    className="px-4 py-2 whitespace-nowrap"
                    style={{ width: column.width || 'auto' }}
                  >
                    {column.render ? column.render(row, column) : renderCell(row[column.id || column.key], column.type)}
                  </td>
                ))}
              </motion.tr>
            ))}
          </AnimatePresence>
        </tbody>
      </table>
      {typeof totalRecords === 'number' && limit && (onPageChange || onLimitChange) && (
        <div className="p-3 bg-card border-t border-border">
          <Pagination
            currentPage={page || 1}
            totalItems={totalRecords}
            itemsPerPage={limit}
            onPageChange={onPageChange}
            onLimitChange={onLimitChange}
          />
        </div>
      )}
    </div>
  );
};

const renderCell = (value, type) => {
  if (value === undefined || value === null) {
    return <span className="text-muted-foreground italic">N/A</span>;
  }

  if (typeof value === 'object' && value !== null) {
    if (value.nombre) return <span className="text-sm">{value.nombre}</span>;
    if (value.id) return <span className="text-sm">ID: {value.id}</span>;
    if (value instanceof Date) {
      return (
        <span className="text-sm">
          {value.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
        </span>
      );
    }
    return <span className="text-muted-foreground italic">[Objeto]</span>;
  }

  try {
    switch (type) {
      case 'number': {
        const n = typeof value === 'number' ? value : Number(value);
        return <span className="font-medium">{isNaN(n) ? '0' : n.toLocaleString()}</span>;
      }
      case 'boolean':
        return value ? (
          <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-semibold border border-green-200">
            Sí
          </span>
        ) : (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold border border-red-200">
            No
          </span>
        );
      case 'date': {
        const d = new Date(value);
        if (isNaN(d.getTime())) return <span className="text-muted-foreground italic">Fecha inválida</span>;
        return (
          <span className="text-sm">
            {d.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' })}
          </span>
        );
      }
      case 'status': {
        const s = String(value).toLowerCase();
        const cls =
          s === 'activo'
            ? 'bg-green-700 text-white border-green-200'
            : s === 'inactivo'
            ? 'bg-red-700 text-white border-red-200'
            : s === 'suspendido'
            ? 'bg-amber-100 text-amber-700 border-amber-200'
            : 'bg-muted text-foreground border-border';
        return (
          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${cls}`}>
            {String(value)}
          </span>
        );
      }
      default:
        return <span className="text-sm">{String(value)}</span>;
    }
  } catch (err) {
    console.error('renderCell error:', err);
    return <span className="text-muted-foreground italic">—</span>;
  }
};

export default DataTable;
